import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  Mail,
  Settings,
  Activity,
  Sparkles,
  UsersRound,
  MessageSquare,
  Webhook,
  BookOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Brain,
  FileSpreadsheet,
  Send,
  ClipboardList,
  AlertTriangle,
  ListChecks,
  BarChart3,
  Layers,
  Bell
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * Admin Dashboard Wrapper with collapsible vertical sidebar
 */
function AdminDashboardWrapper({ children }) {
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('adminSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    email: false,
    tools: false
  });

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const navGroups = [
    {
      title: 'Main',
      items: [
        { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
        { href: '/admin/participants', label: 'Participants', icon: Users },
        { href: '/admin/cohorts', label: 'Cohorts', icon: UsersRound },
        { href: '/admin/bookings', label: 'Bookings', icon: Calendar },
        { href: '/admin/applications', label: 'Applications', icon: ClipboardList },
      ]
    },
    {
      title: 'Email & Templates',
      key: 'email',
      collapsible: true,
      icon: Mail,
      items: [
        { href: '/admin/email-templates', label: 'Email Templates', icon: FileText },
        { href: '/admin/email-sequences', label: 'Email Sequences', icon: Send },
        { href: '/admin/email-logs', label: 'Email Logs', icon: Activity },
        { href: '/admin/pdf-templates', label: 'PDF Templates', icon: FileSpreadsheet },
      ]
    },
    {
      title: 'Tools & Insights',
      key: 'tools',
      collapsible: true,
      icon: Sparkles,
      items: [
        { href: '/admin/ai-insights', label: 'AI Insights', icon: Sparkles },
        { href: '/admin/flagged-sessions', label: 'Flagged Sessions', icon: AlertTriangle },
        { href: '/admin/session-templates', label: 'Session Templates', icon: ListChecks },
        { href: '/facilitator', label: 'Facilitator Mode', icon: Users },
        { href: '/admin/prompts', label: 'AI Prompts', icon: Brain },
        { href: '/admin/testimonials', label: 'Testimonials', icon: MessageSquare },
        { href: '/admin/content', label: 'Content Library', icon: BookOpen },
      ]
    },
    {
      title: 'Analytics & Reports',
      key: 'analytics',
      collapsible: true,
      icon: BarChart3,
      items: [
        { href: '/admin/reports', label: 'Custom Reports', icon: FileSpreadsheet },
        { href: '/admin/cohort-comparison', label: 'Cohort Comparison', icon: Layers },
      ]
    },
    {
      title: 'System',
      items: [
        { href: '/admin/notifications', label: 'Notifications', icon: Bell },
        { href: '/admin/activity-logs', label: 'Activity Logs', icon: Activity },
        { href: '/admin/webhooks', label: 'Webhooks', icon: Webhook },
        { href: '/admin/settings', label: 'Settings', icon: Settings },
      ]
    }
  ];

  const NavItem = ({ item, collapsed }) => {
    const Icon = item.icon;
    const isActive = window.location.pathname === item.href;

    const content = (
      <a
        href={item.href}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200",
          isActive 
            ? "bg-primary text-primary-foreground shadow-sm" 
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
          collapsed && "justify-center px-2"
        )}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <Icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-primary-foreground")} />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </a>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {content}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  const SidebarContent = ({ collapsed, isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo Area */}
      <div className={cn(
        "flex items-center gap-3 px-4 py-5 border-b border-border",
        collapsed && "justify-center px-2"
      )}>
        {collapsed ? (
          <a href="/admin" className="flex items-center">
            <Logo variant="icon" size="sm" />
          </a>
        ) : (
          <a href="/admin" className="flex items-center gap-2" data-testid="admin-logo">
            <Logo variant="full" size="sm" />
            <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              Admin
            </span>
          </a>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        <TooltipProvider>
          {navGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="space-y-1">
              {/* Group Header */}
              {group.collapsible ? (
                <button
                  onClick={() => !collapsed && toggleGroup(group.key)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors rounded-md",
                    collapsed && "justify-center"
                  )}
                >
                  {collapsed ? (
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <group.icon className="w-4 h-4" />
                      </TooltipTrigger>
                      <TooltipContent side="right">{group.title}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <>
                      <group.icon className="w-4 h-4" />
                      <span className="flex-1 text-left">{group.title}</span>
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform duration-200",
                        expandedGroups[group.key] && "rotate-180"
                      )} />
                    </>
                  )}
                </button>
              ) : (
                !collapsed && (
                  <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.title}
                  </div>
                )
              )}

              {/* Group Items */}
              {(!group.collapsible || expandedGroups[group.key] || collapsed) && (
                <div className={cn(
                  "space-y-1",
                  group.collapsible && !collapsed && "ml-2 pl-2 border-l border-border"
                )}>
                  {group.items.map((item) => (
                    <NavItem key={item.href} item={item} collapsed={collapsed} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </TooltipProvider>
      </nav>

      {/* User Section */}
      <div className={cn(
        "border-t border-border p-4 space-y-3",
        collapsed && "px-2"
      )}>
        {!collapsed && (
          <div className="flex items-center gap-3 px-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold text-primary">
                {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.firstName || 'Admin'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
        )}
        
        <div className={cn("flex items-center gap-2", collapsed ? "flex-col" : "px-2")}>
          <ThemeToggle />
          {collapsed ? (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={logout}
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                    data-testid="admin-logout-button"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Logout</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 -ml-2 text-muted-foreground hover:text-foreground"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <a href="/admin" className="flex items-center gap-2">
            <Logo variant="full" size="sm" />
            <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              Admin
            </span>
          </a>
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div 
            className="absolute left-0 top-0 bottom-0 w-72 bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent collapsed={false} isMobile={true} />
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside 
          className={cn(
            "hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-40 bg-card border-r border-border transition-all duration-300 ease-in-out",
            sidebarCollapsed ? "w-[72px]" : "w-64"
          )}
        >
          <SidebarContent collapsed={sidebarCollapsed} />
          
          {/* Collapse Toggle Button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-20 p-1.5 bg-card border border-border rounded-full shadow-md hover:bg-muted transition-colors"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
        </aside>

        {/* Main Content */}
        <main 
          className={cn(
            "flex-1 min-h-screen transition-all duration-300 ease-in-out",
            sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-64"
          )}
        >
          {/* Desktop Top Bar */}
          <header className="hidden lg:flex items-center justify-between sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 px-6 py-3">
            <div>
              {/* Breadcrumb could go here */}
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
            </div>
          </header>

          {/* Page Content */}
          <div className="p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminDashboardWrapper;
